import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faExclamation, faInfo, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Snackbar, SnackbarProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectSnackbar, setSnackbar } from '@redux/slices/snackbarSlice';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SnackbarTsoPqProps extends SnackbarProps {
  type?: 'success' | 'warning' | 'error' | 'information';
  textContent?: string;
  copyToClipboard?: boolean;
  requestID?: string;
}

const StyledSnackbar = styled(Snackbar, {
  shouldForwardProp: (prop) => !['type', 'textContent', 'copyToClipboard', 'requestID'].includes(prop as string),
})<SnackbarTsoPqProps>(() => {
  const { type } = useAppSelector(selectSnackbar);

  const getBackgroundColor = (type: string) => {
    const { palette } = tsoPqTheme;

    switch (type) {
      case 'success':
        return palette.customInformation.color_signal_success_success_bg_accent;
      case 'warning':
        return palette.customInformation.color_signal_warning_warning_bg_accent;
      case 'information':
        return palette.customInformation.color_signal_information_information_bg_accent;
      case 'error':
      default:
        return palette.customError.color_signal_error_error_bg_accent;
    }
  };

  return {
    background: getBackgroundColor(type),
    position: 'fixed',
    left: 'auto',

    right: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_12,
    top: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
    fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
    fontFamily: tsoPqTheme.typography.openSans,
    lineHeight: '1.25rem',
    maxWidth: '32.5rem',
    textWrap: 'wrap',
    wordBreak: 'break-word',
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    boxShadow: tsoPqTheme.customShadows.elevation_1,
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    '& .MuiSnackbarContent-root': {
      gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_com_gap_xs,
      background: 'transparent',
      padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_com_padding_m,
      boxShadow: 'none',
      color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    },
    '& .MuiSnackbarContent-message ': {
      padding: 0,
    },
  };
});

const StyledTextContentWrapper = styled(Typography)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24,
  };
});

const StyledTextContent = styled(Typography)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_12,
    fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
  };
});
const StyledRequestIDContent = styled(Typography)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
    width: '100%',
    justifyContent: 'flex-start',
  };
});
const StyledRequestIDContentText = styled(Typography)(() => {
  return {
    display: 'flex',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
  };
});

const SnackbarTsoPq = ({ ...props }: SnackbarTsoPqProps) => {
  const { open, type, textContent, copyToClipboard, requestID } = useAppSelector(selectSnackbar);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = (text?: string) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy stuffy stuff: ', err);
      });
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return faCheck;
      case 'warning':
        return faExclamation;
      case 'information':
        return faInfo;
      case 'error':
      default:
        return faXmark;
    }
  };

  const getIconColor = (type: string) => {
    const { palette } = tsoPqTheme;

    switch (type) {
      case 'success':
        return palette.customInformation.color_signal_success_success;
      case 'warning':
        return palette.customInformation.color_signal_warning_warning;
      case 'information':
        return palette.customInformation.color_signal_information_information;
      case 'error':
      default:
        return palette.customError.color_signal_error_error;
    }
  };

  const handleClose = () => {
    dispatch(
      setSnackbar({
        open: false,
      })
    );
  };

  return (
    <StyledSnackbar
      {...props}
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      type={type}
      onClose={handleClose}
      message={
        <StyledTextContentWrapper>
          <StyledTextContent>
            <FontAwesomeIcon
              icon={getIcon(type)}
              style={{ fontSize: tsoPqTheme.typography.fontsize_title_list_section_large, color: getIconColor(type) }}
            />
            {textContent}
          </StyledTextContent>
          {type == 'error' && copyToClipboard && (
            <StyledRequestIDContent>
              <IconButtonTsoPq
                buttonType="transparentWithCustomColor"
                sizeVariant="s"
                tooltip={t('snackbar.copyToClipBoard')}
                customColor={getIconColor(type)}
                icon={copied ? faCheck : faCopy}
                onClick={() => handleCopyToClipboard(requestID)}
              />
              <StyledRequestIDContentText>
                <TypographyTsoPq typographyType="labelSMBold">Request ID:</TypographyTsoPq>
                <TypographyTsoPq typographyType="labelSM">{requestID}</TypographyTsoPq>
              </StyledRequestIDContentText>
            </StyledRequestIDContent>
          )}
        </StyledTextContentWrapper>
      }
    ></StyledSnackbar>
  );
};

export default SnackbarTsoPq;
